import React, { lazy, Suspense } from "react";
import Navbar from "@/components/Navbar";
import InsuranceHero from "@/components/InsuranceHero";
import { CLINIC_REF } from "@/components/schemaConstants";
import ClinicalReviewNote from "./ClinicalReviewNote";

// Below-fold sections — lazy-loaded so their JS (react-hook-form insurance
// verification form, Radix accordion FAQs, crisis banner, disclaimer, footer)
// ships in separate chunks that load after hydration, not in the initial
// page chunk. TanStack Start's streaming SSR still renders their content for
// crawlers/AI. This template is shared by ~67 insurer pages, so the win
// compounds across the whole insurance route group.
const InsuranceBodyTop = lazy(() =>
  import("@/components/InsuranceBodyTop").then((m) => ({
    default: m.InsuranceBodyTop,
  })),
);
const InsuranceBodyBottom = lazy(() =>
  import("@/components/InsuranceBodyBottom").then((m) => ({
    default: m.InsuranceBodyBottom,
  })),
);
const CrisisLifelineBanner = lazy(
  () => import("@/components/CrisisLifelineBanner"),
);
const InsurerDisclaimer = lazy(() => import("@/components/InsurerDisclaimer"));
const Footer = lazy(() => import("@/components/sections/Footer"));

export interface InsuranceFAQ {
  question: string;
  answer: string;
}

export interface InsurancePageConfig {
  providerName: string;
  shortName: string;
  slug: string;
  logoUrl?: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  acceleratedDescription?: string;
  planSpecificIntro?: string;
  planType?: string;
  planRegion?: string;
  memberTerm?: string;
  behavioralHealthTerm?: string;
  faqs?: InsuranceFAQ[];
  officialResourceUrl?: string;
  officialResourceLabel?: string;
  /** E-E-A-T: named medical reviewer (e.g. "Daniel Pietras, MD"). */
  reviewedBy?: string;
  /** E-E-A-T: ISO 8601 date of last clinical review. */
  lastUpdated?: string;
}

const defaultFAQs = (shortName: string): InsuranceFAQ[] => [
  {
    question: `Does ${shortName} cover TMS?`,
    answer: `Coverage varies by individual plan. ${shortName} members may have benefits that can apply to TMS therapy, but approval depends on your specific policy, diagnosis, and medical necessity documentation. Our team can help verify your benefits and explain the next steps.`,
  },
  {
    question: "Does my diagnosis affect coverage?",
    answer:
      "Yes. Insurance companies may have specific medical-necessity and eligibility requirements for TMS and other neuromodulation treatments. A confirmed diagnosis of treatment-resistant depression is commonly required, along with documentation of prior antidepressant trials.",
  },
  {
    question: "Do I need prior authorization?",
    answer: `Some plans may require prior authorization for TMS and accelerated TMS protocols. This will be verified based on your individual plan. Our team handles the prior authorization process on your behalf, including submitting clinical documentation and managing appeals if needed.`,
  },
  {
    question: "Do I need to have tried medication first?",
    answer: `Some insurance plans have treatment-history requirements, typically requiring documentation of adequate trials of two or more antidepressants from different classes. Our team can review your situation and explain what information may be needed for your ${shortName} plan.`,
  },
  {
    question: "Can I receive SAINT through my insurance?",
    answer: `SAINT eligibility and insurance coverage depend on the specific plan and clinical circumstances. SAINT bills under Category III CPT codes, and coverage is still developing across the industry. Our team can help determine what options may be available through your ${shortName} plan.`,
  },
  {
    question: `Do you accept my specific ${shortName} plan?`,
    answer:
      "Plan participation and benefits can vary even within the same insurance carrier. Submit your insurance information so our team can verify your specific plan, including network status, copays, deductibles, and coverage for TMS and related services.",
  },
  {
    question: "Do I have to travel to Florida?",
    answer:
      "Neuro Wellness TMS Centers of America is located in South Florida. Some patients travel to our center for treatment, particularly for accelerated protocols like SAINT that can be completed in one week. Our team can discuss logistics, travel arrangements, and available options.",
  },
  {
    question: "How do I get started?",
    answer: `Complete the insurance verification form on this page or call us at (954) 827-9793. Our team will review your ${shortName} benefits, discuss your treatment history, and help you understand what options may be available.`,
  },
];

/**
 * Build the page-level JSON-LD graphs for an insurer coverage page.
 *
 * Single source of truth — consumed by each insurer route's `head().scripts`
 * (server-rendered in <head>) so the MedicalWebPage (+ FAQPage when carrier
 * FAQs are provided) ships in the document head rather than in the body.
 */
export function buildInsuranceSchema(config: InsurancePageConfig): object[] {
  const { shortName, seoDescription, faqs } = config;
  const usingCustomFAQs = !!faqs;
  const faqItems = faqs || defaultFAQs(shortName);
  const blocks: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: `${shortName} Coverage for TMS & Mental Health Treatment`,
      description: seoDescription,
      about: [
        "TMS Therapy",
        "SAINT TMS",
        "Depression Treatment",
        shortName,
        "Insurance Coverage",
      ],
      provider: CLINIC_REF,
    },
  ];
  if (usingCustomFAQs) {
    blocks.unshift({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.answer,
        },
      })),
    });
  }
  return blocks;
}

const InsurancePageTemplate: React.FC<{
  config: InsurancePageConfig;
}> = ({ config }) => {
  const {
    providerName,
    shortName,
    acceleratedDescription,
    planSpecificIntro,
    planType,
    planRegion,
    memberTerm = "members",
    faqs,
    officialResourceUrl,
    officialResourceLabel,
    reviewedBy,
    lastUpdated,
  } = config;
  const faqItems = faqs || defaultFAQs(shortName);
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16 sm:pt-20">
        <InsuranceHero
          providerName={providerName}
          shortName={shortName}
          memberTerm={memberTerm}
        />
        {(reviewedBy || lastUpdated) && (
          <div className="bg-background pt-6">
            <div className="container-px mx-auto max-w-4xl px-4">
              <ClinicalReviewNote
                reviewedBy={reviewedBy}
                lastUpdated={lastUpdated}
              />
            </div>
          </div>
        )}
        <Suspense fallback={null}>
          <InsuranceBodyTop
            providerName={providerName}
            shortName={shortName}
            memberTerm={memberTerm}
            planType={planType}
            planRegion={planRegion}
            planSpecificIntro={planSpecificIntro}
            acceleratedDescription={acceleratedDescription}
          />
        </Suspense>
        <Suspense fallback={null}>
          <InsuranceBodyBottom
            providerName={providerName}
            shortName={shortName}
            faqItems={faqItems}
            officialResourceUrl={officialResourceUrl}
            officialResourceLabel={officialResourceLabel}
          />
        </Suspense>
      </main>
      <div className="container-px mx-auto max-w-4xl px-4 py-8">
        <Suspense fallback={null}>
          <CrisisLifelineBanner className="mb-4" />
        </Suspense>
        <Suspense fallback={null}>
          <InsurerDisclaimer shortName={shortName} />
        </Suspense>
      </div>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default InsurancePageTemplate;
